import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  IconButton,
  MenuItem,
  Box,
  Stack,
  ImageList,
  ImageListItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams } from "react-router-dom";
import { Product, ProductImage, Specification } from "../types/Product";
import * as api from "../services/api";
import { config } from "../config/environment";
import { log, handleApiError } from "../utils/helpers";

interface Category {
  id: number;
  name: string;
}

interface ProductFormProps {
  initialData?: Product;
}

const ProductForm: React.FC<ProductFormProps> = ({ initialData }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    specifications: [
      { key: "", value: "", unit: "" as string | undefined },
    ] as Specification[],
    advantages: [""],
    image: "",
    images: [] as ProductImage[],
    categoryId: "",
    isFeatured: false,
  });

  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchProduct();
    }
  }, [id]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        specifications: parseJSON<Specification[]>(initialData.specifications, [
          { key: "", value: "", unit: "" },
        ]),
        advantages: parseJSON<string[]>(initialData.advantages, [""]),
        categoryId: initialData.categoryId.toString(),
        images: initialData.images || [],
        isFeatured: initialData.isFeatured || false,
      });
    }
  }, [initialData]);

  const parseJSON = <T,>(jsonString: string, fallback: T): T => {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.error("Error parsing JSON:", e);
      return fallback;
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/categories`);
      const data = await response.json();
      setCategories(data);
      log("Categories fetched successfully", "debug", { count: data.length });
    } catch (error) {
      const errorMessage = handleApiError(error);
      log("Error fetching categories", "error", { error: errorMessage });
      console.error("Error fetching categories:", error);
    }
  };

  const fetchProduct = async () => {
    if (!id) return;
    try {
      const data = await api.getProduct(parseInt(id));
      setFormData({
        ...data,
        specifications: parseJSON<Specification[]>(data.specifications, [
          { key: "", value: "", unit: "" },
        ]),
        advantages: parseJSON<string[]>(data.advantages, [""]),
        categoryId: data.categoryId.toString(),
        images: data.images || [],
        isFeatured: data.isFeatured || false,
      });
    } catch (error) {
      console.error("Error fetching product:", error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSpecificationChange = (
    index: number,
    field: "key" | "value" | "unit",
    value: string
  ) => {
    const newSpecifications = [...formData.specifications];
    newSpecifications[index][field] = value;
    setFormData({
      ...formData,
      specifications: newSpecifications,
    });
  };

  const addSpecification = () => {
    setFormData({
      ...formData,
      specifications: [
        ...formData.specifications,
        { key: "", value: "", unit: "" as string | undefined },
      ],
    });
  };

  const removeSpecification = (index: number) => {
    const newSpecifications = formData.specifications.filter(
      (_, i) => i !== index
    );
    setFormData({
      ...formData,
      specifications: newSpecifications,
    });
  };

  const handleAdvantageChange = (index: number, value: string) => {
    const newAdvantages = [...formData.advantages];
    newAdvantages[index] = value;
    setFormData({
      ...formData,
      advantages: newAdvantages,
    });
  };

  const addAdvantage = () => {
    setFormData({
      ...formData,
      advantages: [...formData.advantages, ""],
    });
  };

  const removeAdvantage = (index: number) => {
    const newAdvantages = formData.advantages.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      advantages: newAdvantages,
    });
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      try {
        const response = await api.uploadImages(Array.from(files));

        // Update main image if it's the first upload and no main image exists
        if (response.urls.length > 0 && !formData.image) {
          setFormData((prev) => ({
            ...prev,
            image: response.urls[0],
            images: [
              ...prev.images,
              // Only add the remaining URLs to additional images, not the main image
              ...response.urls.slice(1).map((url: string) => ({ url })),
            ],
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            images: [
              ...prev.images,
              ...response.urls.map((url: string) => ({ url })),
            ],
          }));
        }
      } catch (error) {
        console.error("Error uploading images:", error);
      }
    }
  };

  const removeImage = async (index: number) => {
    const image = formData.images[index];

    // Try to delete from backend if image has ID and product exists
    if (image.id && id) {
      try {
        await api.deleteImage(parseInt(id), image.id);
        log("Image deleted from backend successfully", "debug", {
          productId: id,
          imageId: image.id,
        });
      } catch (error: any) {
        const errorMessage = handleApiError(error);
        log("Error deleting image from backend", "error", {
          error: errorMessage,
          productId: id,
          imageId: image.id,
        });

        // Show user-friendly error message
        alert(`Error deleting image: ${errorMessage}`);
        return; // Don't remove from local state if backend deletion failed
      }
    }

    // Remove from local state
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));

    // If we're removing the main image, set the next available image as main
    if (image.url === formData.image && formData.images.length > 1) {
      const nextImage = formData.images.find((_, i) => i !== index);
      if (nextImage) {
        setFormData((prev) => ({
          ...prev,
          image: nextImage.url,
        }));
      }
    }

    log("Image removed from form", "debug", {
      remainingImages: formData.images.length - 1,
    });
  };

  const setAsMainImage = (imageUrl: string) => {
    setFormData((prev) => ({
      ...prev,
      image: imageUrl,
    }));
    log("Main image changed", "debug", { newMainImage: imageUrl });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        specifications: JSON.stringify(formData.specifications),
        advantages: JSON.stringify(formData.advantages),
        categoryId: parseInt(formData.categoryId as string),
        isFeatured: formData.isFeatured,
      };

      if (id) {
        await api.updateProduct(parseInt(id), submitData);
      } else {
        await api.createProduct(submitData);
      }

      navigate("/");
    } catch (error: any) {
      console.error("Error saving product:", error);
      // Show user-friendly error message
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to save product";
      alert(errorMessage);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {id ? "Edit Product" : "Add Product"}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            name="name"
            label="Product Name"
            value={formData.name}
            onChange={handleChange}
            required
          />

          <TextField
            name="description"
            label="Description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={4}
            required
          />

          <TextField
            select
            name="categoryId"
            label="Category"
            value={formData.categoryId}
            onChange={handleChange}
            required
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>

          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 2,
              bgcolor: "#f8f9fa",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isFeatured}
                  onChange={(e) =>
                    setFormData({ ...formData, isFeatured: e.target.checked })
                  }
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Jadikan Produk Unggulan
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Produk ini akan ditampilkan di halaman utama (maksimal 6
                    produk)
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Spesifikasi Produk
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Masukkan detail spesifikasi produk. Field satuan bersifat
              opsional.
            </Typography>
            {formData.specifications.map((spec, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  gap: 2,
                  mb: 2,
                  alignItems: "flex-start",
                  p: 2,
                  bgcolor: index % 2 === 0 ? "#f8f9fa" : "white",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Nama Spesifikasi
                  </Typography>
                  <TextField
                    placeholder="Contoh: Berat Kosong Dump"
                    value={spec.key}
                    onChange={(e) =>
                      handleSpecificationChange(index, "key", e.target.value)
                    }
                    size="small"
                    fullWidth
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Nilai
                  </Typography>
                  <TextField
                    placeholder="Contoh: 700"
                    value={spec.value}
                    onChange={(e) =>
                      handleSpecificationChange(index, "value", e.target.value)
                    }
                    size="small"
                    fullWidth
                  />
                </Box>
                <Box
                  sx={{
                    flex: 0.6,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Satuan (Opsional)
                  </Typography>
                  <TextField
                    placeholder="kg, mm, liter"
                    value={spec.unit || ""}
                    onChange={(e) =>
                      handleSpecificationChange(index, "unit", e.target.value)
                    }
                    size="small"
                    fullWidth
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    height: "100%",
                    pt: 2.5,
                  }}
                >
                  <IconButton
                    onClick={() => removeSpecification(index)}
                    color="error"
                    size="small"
                    sx={{
                      "&:hover": {
                        bgcolor: "error.light",
                        color: "white",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
            <Button
              onClick={addSpecification}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              + Tambah Spesifikasi
            </Button>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Advantages
            </Typography>
            {formData.advantages.map((advantage, index) => (
              <Box key={index} sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label={`Advantage ${index + 1}`}
                  value={advantage}
                  onChange={(e) => handleAdvantageChange(index, e.target.value)}
                />
                <IconButton
                  onClick={() => removeAdvantage(index)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button onClick={addAdvantage}>Add Advantage</Button>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Main Product Image
            </Typography>
            {formData.image && (
              <Box sx={{ mb: 2 }}>
                <img
                  src={api.getImageUrl(formData.image)}
                  alt="Main Product"
                  style={{
                    height: "200px",
                    objectFit: "cover",
                    width: "200px",
                    borderRadius: "8px",
                  }}
                />
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Main Image URL: {formData.image}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The first image uploaded will automatically become the main image.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Additional Product Images
            </Typography>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageChange}
              style={{ marginBottom: "1rem" }}
            />
            {formData.images.length > 0 && (
              <ImageList
                sx={{ width: "100%", height: "auto" }}
                cols={3}
                rowHeight={200}
              >
                {formData.images.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={api.getImageUrl(image.url)}
                      alt={`Product ${index + 1}`}
                      loading="lazy"
                      style={{
                        height: "200px",
                        objectFit: "cover",
                        width: "100%",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bgcolor: "rgba(0,0,0,0.5)",
                        borderRadius: "0 0 0 8px",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => removeImage(index)}
                        sx={{ color: "white" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: "rgba(0,0,0,0.5)",
                        color: "white",
                        padding: "4px",
                        textAlign: "center",
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setAsMainImage(image.url)}
                        sx={{
                          fontSize: "0.7rem",
                          minWidth: "auto",
                          padding: "2px 8px",
                        }}
                      >
                        Set as Main
                      </Button>
                    </Box>
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </Box>

          <Button type="submit" variant="contained" color="primary">
            {id ? "Update Product" : "Create Product"}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default ProductForm;
